import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Icon } from '../../utils/icons';
import { useNavigate } from 'react-router-dom';
import ColorPicker, { ColorPresets } from './ColorPicker';
import ImageCropper from '../ImageCropper';

const WhatsAppWidgetConfig = ({
    widgetConfig,
    onWidgetConfigChange,
    onCreateWidget,
    isLoading,
    onClose,
    onBack: _onBack
}) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('connect');
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);
    const [showProfileGallery, setShowProfileGallery] = useState(false);
    const [profileImages, setProfileImages] = useState([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [showImageCropper, setShowImageCropper] = useState(false);
    const [isChatWindowOpen, setIsChatWindowOpen] = useState(true);
    const [previewInputValue, setPreviewInputValue] = useState(widgetConfig.whatsappConfig?.message || '');
    const inputRef = useRef(null);

    // Handle input change with focus preservation
    const handleInputChange = useCallback((e) => {
        e.stopPropagation();
        setPreviewInputValue(e.target.value);
    }, []);

    // Handle send message
    const handleSendMessage = useCallback(() => {
        if (previewInputValue.trim()) {
            const phoneNumber = widgetConfig.whatsappConfig?.phoneNumber || '';
            const encodedMessage = encodeURIComponent(previewInputValue);
            const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

            // Open WhatsApp URL in new window
            window.open(whatsappUrl, '_blank');

            // Reset to pre-filled message
            setPreviewInputValue(widgetConfig.whatsappConfig?.message || '');

            // Maintain focus after clearing
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    }, [previewInputValue, widgetConfig.whatsappConfig?.phoneNumber, widgetConfig.whatsappConfig?.message]);

    // Handle key press
    const handleKeyDown = useCallback((e) => {
        e.stopPropagation();
        if (e.key === 'Enter' && previewInputValue.trim()) {
            e.preventDefault();
            handleSendMessage();
        }
    }, [previewInputValue, handleSendMessage]);

    // Update preview input when message config changes
    useEffect(() => {
        setPreviewInputValue(widgetConfig.whatsappConfig?.message || '');
    }, [widgetConfig.whatsappConfig?.message]);

    // Custom back function for WhatsApp widget - go to dashboard instead of Step 1
    const handleBack = () => {
        navigate('/dashboard');
    };

    // Fetch random profile images
    const fetchProfileImages = async () => {
        setLoadingImages(true);
        try {
            const response = await fetch('https://randomuser.me/api/?inc=picture&page=1&results=100');
            const data = await response.json();
            setProfileImages(data.results);
        } catch (error) {
            console.error('Error fetching profile images:', error);
        } finally {
            setLoadingImages(false);
        }
    };

    const tabs = [
        { id: 'connect', name: 'Connect', icon: 'link' },
        { id: 'content', name: 'Content', icon: 'whatsapp' },
        { id: 'settings', name: 'Settings', icon: 'settings' },
        { id: 'style', name: 'Style', icon: 'palette' }
    ];

    // Update widget configuration
    const updateConfig = (updates) => {
        onWidgetConfigChange({
            ...widgetConfig,
            ...updates
        });
    };

    // WhatsApp Widget Preview Component
    const WhatsAppWidgetPreview = React.memo(() => {
        const config = widgetConfig.whatsappConfig || {};
        const style = widgetConfig.style || {};

        const position = config.position || 'bottom-right';
        const size = config.size || 'medium';
        const showTooltip = config.showTooltip !== false;
        const tooltipText = config.tooltipText || 'Chat with us!';
        const backgroundColor = style.backgroundColor || '#25D366';
        const name = config.name || 'Support Team';
        const profilePicture = config.profilePicture;
        const captionText = config.captionType === 'custom' ? config.captionText :
            config.captionType === 'online' ? 'Online' :
                config.captionType || 'Online';

        const positionClasses = {
            'bottom-right': 'bottom-6 right-6',
            'bottom-left': 'bottom-6 left-6',
            'top-right': 'top-6 right-6',
            'top-left': 'top-6 left-6'
        };

        const chatWindowPositionClasses = {
            'bottom-right': 'bottom-24 right-2',
            'bottom-left': 'bottom-24 left-2',
            'top-right': 'top-2 right-2',
            'top-left': 'top-2 left-2'
        };

        const sizeClasses = {
            small: 'w-12 h-12',
            medium: 'w-16 h-16',
            large: 'w-20 h-20'
        };

        return (
            <div className="relative w-full h-80 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                {/* Background pattern to simulate website */}
                <div className="absolute inset-0 opacity-10">
                    <div className="grid grid-cols-4 h-full">
                        {Array.from({ length: 16 }).map((_, i) => (
                            <div key={i} className="border border-gray-400"></div>
                        ))}
                    </div>
                </div>

                {/* Website Content Simulation */}
                <div className="absolute top-4 left-4 right-4">
                    <div className="bg-white rounded shadow-sm p-3 mb-2 opacity-60">
                        <div className="h-2 bg-gray-300 rounded mb-2"></div>
                        <div className="h-2 bg-gray-300 rounded w-3/4"></div>
                    </div>
                    <div className="bg-white rounded shadow-sm p-3 opacity-40">
                        <div className="h-2 bg-gray-300 rounded mb-2"></div>
                        <div className="h-2 bg-gray-300 rounded w-1/2"></div>
                    </div>
                </div>

                {/* WhatsApp Chat Window (Open State) */}
                {isChatWindowOpen && (
                    <div key="whatsapp-chat-window" className={`absolute ${chatWindowPositionClasses[position]} w-72 max-w-[calc(100%-2rem)] z-50`}>
                        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200" onClick={(e) => e.stopPropagation()}>
                            {/* Header */}
                            <div className="px-4 py-3 text-white relative" style={{ backgroundColor: '#008069' }}>
                                <button
                                    className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors"
                                    onClick={() => setIsChatWindowOpen(false)}
                                >
                                    <Icon name="close" size={20} />
                                </button>
                                <div className="flex items-center space-x-3 pr-8">
                                    <div className="relative">
                                        <div className="w-10 h-10 rounded-full overflow-hidden bg-white/20 flex items-center justify-center">
                                            {profilePicture ? (
                                                <img
                                                    src={profilePicture}
                                                    alt="Profile"
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        console.error('Preview profile picture failed to load:', e.target.src);
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            ) : (
                                                <Icon name="person" className="text-white text-lg" size={18} />
                                            )}
                                            {profilePicture && (
                                                <Icon name="person" className="text-white text-lg" style={{ display: 'none' }} size={18} />
                                            )}
                                        </div>
                                        <div className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: '#25D366' }}></div>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-white">{name}</h3>
                                        <p className="text-xs text-white/80">{captionText}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Chat Area */}
                            <div className="p-4 h-24 relative"
                                style={{
                                    backgroundImage: `url('/background-whatsapp.jpg')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat'
                                }}>
                                {/* Message Bubble */}
                                <div className="flex justify-start">
                                    <div className="bg-white rounded-2xl rounded-tl-sm px-3 py-2 max-w-xs shadow-sm">
                                        <p className="text-gray-800 text-xs whitespace-pre-line">
                                            {widgetConfig.whatsappConfig?.welcomeMessage || 'Hi there 👋\n\nHow can I help you?'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Message Input */}
                            <div className="px-3 py-2 bg-white border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center space-x-2 bg-gray-50 rounded-full px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        key="whatsapp-preview-input"
                                        ref={inputRef}
                                        type="text"
                                        placeholder={widgetConfig.whatsappConfig?.message || "Type a message..."}
                                        className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 border-none outline-none focus:outline-none focus:ring-0 focus:border-transparent py-1"
                                        value={previewInputValue}
                                        onChange={handleInputChange}
                                        onKeyDown={handleKeyDown}
                                        onClick={(e) => e.stopPropagation()}
                                        onFocus={(e) => e.stopPropagation()}
                                        autoComplete="off"
                                    />
                                    <button
                                        className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors group"
                                        style={{ backgroundColor: '#008069' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSendMessage();
                                        }}
                                    >
                                        <Icon name="forward" className="text-white transform group-hover:scale-110 transition-transform" size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* WhatsApp Floating Button (Original UI) */}
                <div className={`absolute ${positionClasses[position]} group`}>
                    {/* Tooltip */}
                    {showTooltip && (
                        <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="bg-gray-900 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap">
                                {tooltipText}
                                <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                            </div>
                        </div>
                    )}

                    {/* WhatsApp Button */}
                    <button
                        className={`${sizeClasses[size]} rounded-full shadow-lg transform hover:scale-110 transition-all duration-200 flex items-center justify-center hover:shadow-xl`}
                        style={{ backgroundColor, color: '#FFFFFF' }}
                        onClick={() => setIsChatWindowOpen(!isChatWindowOpen)}
                    >
                        <img
                            src="/whatsapp_icon.svg"
                            alt="WhatsApp"
                            className={`${size === 'small' ? 'w-8 h-8' :
                                size === 'medium' ? 'w-10 h-10' :
                                    'w-12 h-12'
                                }`}
                        />
                    </button>

                    {/* Pulse Animation */}
                    <div className="absolute inset-0 rounded-full animate-ping opacity-25 pointer-events-none" style={{ backgroundColor }}></div>
                </div>

                {/* Preview Label */}
                <div className="absolute bottom-2 left-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    Chat Window Preview
                </div>
            </div>
        );
    });

    WhatsAppWidgetPreview.displayName = 'WhatsAppWidgetPreview';

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleBack}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <Icon name="back" size={24} />
                        </button>
                        <div>
                            <div className="flex items-center space-x-3">
                                <img
                                    src="/whatsapp_icon.svg"
                                    alt="WhatsApp"
                                    className="w-8 h-8"
                                />
                                <h1 className="text-xl font-semibold text-gray-900">
                                    {widgetConfig.name || 'WhatsApp Chat Widget'}
                                </h1>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                <span>WhatsApp Widget</span>
                            </div>
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                            className="sm:hidden p-1 rounded-md text-gray-400 hover:text-gray-600"
                        >
                            <Icon name="menu" size={24} />
                        </button>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onCreateWidget}
                            disabled={isLoading}
                            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                        >
                            {isLoading && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            )}
                            {!isLoading && (
                                <img
                                    src="/whatsapp_icon.svg"
                                    alt="WhatsApp"
                                    className="w-4 h-4"
                                />
                            )}
                            <span>{isLoading ? 'Creating...' : 'Create Widget'}</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors hidden sm:block"
                        >
                            <Icon name="close" size={24} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile sidebar overlay */}
            {showMobileSidebar && (
                <div className="fixed inset-0 z-50 sm:hidden">
                    <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowMobileSidebar(false)}></div>
                    <div className="relative flex flex-col w-full max-w-xs bg-gray-800 text-white shadow-xl">
                        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-700">
                            <h2 className="text-lg font-semibold">Configuration</h2>
                            <button
                                onClick={() => setShowMobileSidebar(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                <Icon name="close" size={24} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {/* Mobile tab navigation */}
                            <div className="space-y-2 mb-6">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => {
                                            setActiveTab(tab.id);
                                            setShowMobileSidebar(false);
                                        }}
                                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors relative ${activeTab === tab.id
                                            ? 'bg-gray-700 text-white border-l-4 border-green-500'
                                            : 'hover:bg-gray-700 text-gray-300'
                                            }`}
                                    >
                                        <span className="text-lg">
                                            <Icon name={tab.icon} size={20} />
                                        </span>
                                        <span>{tab.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex h-[calc(100vh-73px)]">
                {/* Left Sidebar - Icon Navigation - Hidden on mobile */}
                <div className="hidden sm:block w-20 bg-gray-800 text-white border-r border-gray-700">
                    <div className="flex flex-col items-center py-6 space-y-3">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-14 h-14 flex flex-col items-center justify-center transition-colors relative ${activeTab === tab.id
                                    ? 'bg-gray-700 text-white'
                                    : 'hover:bg-gray-700 text-gray-400'
                                    }`}
                                title={tab.name}
                            >
                                <span className="text-lg mb-1">
                                    <Icon name={tab.icon} size={20} />
                                </span>
                                <span className="text-xs">{tab.name}</span>
                                {activeTab === tab.id && (
                                    <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-10 bg-green-500 rounded-r shadow-lg"></div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right Sidebar - Content Panel - Responsive */}
                <div className="hidden sm:flex sm:w-80 bg-gray-900 text-white flex-col">
                    <div className="flex-1 overflow-y-auto">
                        {/* Connect Tab */}
                        {activeTab === 'connect' && (
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-4">WhatsApp Connection</h3>
                                <p className="text-sm text-gray-400 mb-6">
                                    Configure your WhatsApp connection settings
                                </p>

                                <div className="space-y-6">
                                    {/* Phone Number */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            WhatsApp Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            placeholder="+1234567890"
                                            value={widgetConfig.whatsappConfig?.phoneNumber || ''}
                                            onChange={(e) => updateConfig({
                                                whatsappConfig: {
                                                    ...widgetConfig.whatsappConfig,
                                                    phoneNumber: e.target.value
                                                }
                                            })}
                                            className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-green-500 focus:outline-none text-sm"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Include country code (e.g., +1)</p>
                                    </div>

                                    {/* Dynamic WhatsApp URL Preview */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Generated WhatsApp URL
                                        </label>
                                        <div className="bg-gray-800 rounded-lg border border-gray-700 p-3">
                                            <code className="text-xs text-green-400 break-all">
                                                {(() => {
                                                    const phoneNumber = widgetConfig.whatsappConfig?.phoneNumber || 'PHONE_NUMBER';
                                                    const message = widgetConfig.whatsappConfig?.message || 'MESSAGE';
                                                    const encodedMessage = encodeURIComponent(message);
                                                    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
                                                })()}
                                            </code>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">This URL will open WhatsApp with the pre-filled message</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Content Tab */}
                        {activeTab === 'content' && (
                            <div className="p-4 max-h-full overflow-y-auto">
                                <h3 className="text-lg font-semibold mb-4">Widget Content</h3>
                                <p className="text-sm text-gray-400 mb-6">
                                    Customize the widget&apos;s display content
                                </p>

                                <div className="space-y-8">
                                    {/* Chat Window Section */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Chat Window</h4>

                                        {/* Profile Picture */}
                                        <div className="mb-6">
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Profile Picture</label>
                                            <div className="flex items-start space-x-3">
                                                {/* Profile Picture Container - Improved for better handling of different sizes */}
                                                <div className="relative">
                                                    <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center border-2 border-gray-600 flex-shrink-0">
                                                        {widgetConfig.whatsappConfig?.profilePicture ? (
                                                            <img
                                                                src={widgetConfig.whatsappConfig.profilePicture}
                                                                alt="Profile"
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    console.error('Profile picture failed to load:', e.target.src);
                                                                    // Remove broken image URL
                                                                    updateConfig({
                                                                        whatsappConfig: {
                                                                            ...widgetConfig.whatsappConfig,
                                                                            profilePicture: ''
                                                                        }
                                                                    });
                                                                }}
                                                                onLoad={() => {
                                                                    console.log('Profile picture loaded successfully:', widgetConfig.whatsappConfig.profilePicture);
                                                                }}
                                                            />
                                                        ) : (
                                                            <span className="text-gray-400 text-xl">👤</span>
                                                        )}
                                                    </div>
                                                    {/* Delete Icon - Only show when profile picture exists */}
                                                    {widgetConfig.whatsappConfig?.profilePicture && (
                                                        <button
                                                            onClick={() => {
                                                                updateConfig({
                                                                    whatsappConfig: {
                                                                        ...widgetConfig.whatsappConfig,
                                                                        profilePicture: ''
                                                                    }
                                                                });
                                                            }}
                                                            className="absolute -top-1 -right-1 w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                                                            title="Remove profile picture"
                                                        >
                                                            <Icon name="close" size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                                {/* Action Buttons - Improved layout */}
                                                <div className="flex flex-col space-y-2 flex-1 min-w-0">
                                                    <div className="flex flex-wrap gap-2">
                                                        <button
                                                            onClick={() => setShowImageCropper(true)}
                                                            className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors flex items-center space-x-1 whitespace-nowrap"
                                                        >
                                                            <Icon name="upload" size={12} />
                                                            <span>Upload</span>
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                if (profileImages.length === 0) {
                                                                    fetchProfileImages();
                                                                }
                                                                setShowProfileGallery(true);
                                                            }}
                                                            className="px-3 py-1.5 bg-gray-800 text-gray-300 text-xs rounded hover:bg-gray-700 transition-colors flex items-center space-x-1 whitespace-nowrap"
                                                        >
                                                            <Icon name="image" size={12} />
                                                            <span>Gallery</span>
                                                        </button>
                                                    </div>
                                                    <p className="text-xs text-gray-400 leading-relaxed">
                                                        Upload your own image or select from gallery. Images are automatically cropped to fit.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Name */}
                                        <div className="mb-6">
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                                            <input
                                                type="text"
                                                placeholder="Support Team"
                                                value={widgetConfig.whatsappConfig?.name || ''}
                                                onChange={(e) => updateConfig({
                                                    whatsappConfig: {
                                                        ...widgetConfig.whatsappConfig,
                                                        name: e.target.value
                                                    }
                                                })}
                                                className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-green-500 focus:outline-none text-sm"
                                            />
                                        </div>

                                        {/* Caption */}
                                        <div className="mb-6">
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Caption</label>
                                            <div className="relative">
                                                <select
                                                    value={widgetConfig.whatsappConfig?.captionType || 'online'}
                                                    onChange={(e) => updateConfig({
                                                        whatsappConfig: {
                                                            ...widgetConfig.whatsappConfig,
                                                            captionType: e.target.value,
                                                            captionText: e.target.value === 'online' ? 'Online' :
                                                                e.target.value === 'custom' ? (widgetConfig.whatsappConfig?.captionText || '') :
                                                                    e.target.value
                                                        }
                                                    })}
                                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-green-500 focus:outline-none text-sm appearance-none"
                                                >
                                                    <option value="online">Online</option>
                                                    <option value="Typically replies instantly">Typically replies instantly</option>
                                                    <option value="Typically replies in minutes">Typically replies in minutes</option>
                                                    <option value="Typically replies within an hour">Typically replies within an hour</option>
                                                    <option value="Typically replies in a few hours">Typically replies in a few hours</option>
                                                    <option value="Typically replies within a day">Typically replies within a day</option>
                                                    <option value="custom">Custom</option>
                                                </select>
                                                <Icon name="dropdown" className="text-gray-400 absolute right-3 top-3 pointer-events-none" size={16} />
                                            </div>
                                            {widgetConfig.whatsappConfig?.captionType === 'custom' && (
                                                <input
                                                    type="text"
                                                    placeholder="Enter custom caption"
                                                    value={widgetConfig.whatsappConfig?.captionText || ''}
                                                    onChange={(e) => updateConfig({
                                                        whatsappConfig: {
                                                            ...widgetConfig.whatsappConfig,
                                                            captionText: e.target.value
                                                        }
                                                    })}
                                                    className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-green-500 focus:outline-none text-sm mt-2"
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Welcome Message Section */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Welcome Message</h4>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Welcome Message
                                        </label>
                                        <textarea
                                            rows={3}
                                            placeholder="Hi there 👋&#10;&#10;How can I help you?"
                                            value={widgetConfig.whatsappConfig?.welcomeMessage || ''}
                                            onChange={(e) => updateConfig({
                                                whatsappConfig: {
                                                    ...widgetConfig.whatsappConfig,
                                                    welcomeMessage: e.target.value
                                                }
                                            })}
                                            className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-green-500 focus:outline-none text-sm resize-none"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">This message will appear in the chat window when it opens</p>
                                    </div>

                                    {/* Pre-filled Message (moved from Connect) */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Pre-filled Message
                                        </label>
                                        <textarea
                                            rows={3}
                                            placeholder="Hi! I&apos;d like to know more about your services."
                                            value={widgetConfig.whatsappConfig?.message || ''}
                                            onChange={(e) => updateConfig({
                                                whatsappConfig: {
                                                    ...widgetConfig.whatsappConfig,
                                                    message: e.target.value
                                                }
                                            })}
                                            className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-green-500 focus:outline-none text-sm resize-none"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Customize the default message that appears in Whatsapp when a user initiates a chat.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Settings Tab */}
                        {activeTab === 'settings' && (
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-4">Widget Settings</h3>
                                <p className="text-sm text-gray-400 mb-6">
                                    Configure widget behavior and positioning
                                </p>

                                <div className="space-y-6">
                                    {/* Position */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-3">
                                            Widget Position
                                        </label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {[
                                                { id: 'bottom-right', name: 'Bottom Right', icon: '↘️' },
                                                { id: 'bottom-left', name: 'Bottom Left', icon: '↙️' },
                                                { id: 'top-right', name: 'Top Right', icon: '↗️' },
                                                { id: 'top-left', name: 'Top Left', icon: '↖️' }
                                            ].map((position) => (
                                                <button
                                                    key={position.id}
                                                    onClick={() => updateConfig({
                                                        whatsappConfig: {
                                                            ...widgetConfig.whatsappConfig,
                                                            position: position.id
                                                        }
                                                    })}
                                                    className={`p-3 text-xs font-medium rounded transition-colors ${(widgetConfig.whatsappConfig?.position || 'bottom-right') === position.id
                                                        ? 'bg-green-600 text-white'
                                                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                                        }`}
                                                >
                                                    <div className="flex flex-col items-center space-y-1">
                                                        <span>{position.icon}</span>
                                                        <span>{position.name}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Style Tab */}
                        {activeTab === 'style' && (
                            <div className="p-4">
                                <h3 className="text-lg font-semibold mb-4">Style</h3>
                                <p className="text-sm text-gray-400 mb-6">
                                    Customize the widget&apos;s visual appearance
                                </p>

                                <div className="space-y-6">
                                    {/* Background Color */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-3">
                                            Background Color
                                        </label>
                                        <ColorPicker
                                            value={widgetConfig.style?.backgroundColor || '#25D366'}
                                            onChange={(color) => updateConfig({
                                                style: {
                                                    ...widgetConfig.style,
                                                    backgroundColor: color
                                                }
                                            })}
                                            label=""
                                            showHex={true}
                                            size="large"
                                        />
                                        <div className="mt-3">
                                            <ColorPresets
                                                selectedColor={widgetConfig.style?.backgroundColor || '#25D366'}
                                                onColorSelect={(color) => updateConfig({
                                                    style: {
                                                        ...widgetConfig.style,
                                                        backgroundColor: color
                                                    }
                                                })}
                                            />
                                        </div>
                                    </div>

                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content - Widget Preview */}
                <div className="flex-1 bg-white p-4 sm:p-8 overflow-y-auto">
                    <div className="max-w-4xl mx-auto">
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Widget Preview</h2>
                            <p className="text-gray-600">See how your WhatsApp widget will appear on your website</p>
                        </div>

                        {/* Widget Preview */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                            <div className="p-6">
                                <WhatsAppWidgetPreview />
                            </div>
                        </div>

                        {/* Widget Information */}
                        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                                <div className="text-green-500">
                                    <Icon name="info" className="text-green-500 mt-0.5" size={20} />
                                </div>
                                <div>
                                    <h3 className="font-medium text-green-800 text-sm">Ready to Connect</h3>
                                    <p className="text-green-700 text-sm mt-1">
                                        Your WhatsApp widget is configured and ready to deploy. Users will be able to contact you directly via WhatsApp by clicking the widget.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Gallery Modal */}
            {showProfileGallery && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-semibold text-gray-900">Select Profile Picture</h3>
                                <button
                                    onClick={() => setShowProfileGallery(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <Icon name="close" size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            {loadingImages ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                                    <span className="ml-3 text-gray-600">Loading images...</span>
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    {/* Men Photos */}
                                    <div>
                                        <h4 className="text-lg font-medium text-gray-900 mb-4">Men</h4>
                                        <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
                                            {profileImages
                                                .filter(person => person.picture.large.includes('/men/'))
                                                .slice(0, 30)
                                                .map((person, index) => (
                                                    <button
                                                        key={`men-${index}`}
                                                        onClick={() => {
                                                            updateConfig({
                                                                whatsappConfig: {
                                                                    ...widgetConfig.whatsappConfig,
                                                                    profilePicture: person.picture.large
                                                                }
                                                            });
                                                            setShowProfileGallery(false);
                                                        }}
                                                        className="aspect-square rounded-full overflow-hidden hover:ring-4 hover:ring-green-200 transition-all duration-200 transform hover:scale-110"
                                                    >
                                                        <img
                                                            src={person.picture.large}
                                                            alt="Profile"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </button>
                                                ))}
                                        </div>
                                    </div>

                                    {/* Women Photos */}
                                    <div>
                                        <h4 className="text-lg font-medium text-gray-900 mb-4">Women</h4>
                                        <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
                                            {profileImages
                                                .filter(person => person.picture.large.includes('/women/'))
                                                .slice(0, 30)
                                                .map((person, index) => (
                                                    <button
                                                        key={`women-${index}`}
                                                        onClick={() => {
                                                            updateConfig({
                                                                whatsappConfig: {
                                                                    ...widgetConfig.whatsappConfig,
                                                                    profilePicture: person.picture.large
                                                                }
                                                            });
                                                            setShowProfileGallery(false);
                                                        }}
                                                        className="aspect-square rounded-full overflow-hidden hover:ring-4 hover:ring-green-200 transition-all duration-200 transform hover:scale-110"
                                                    >
                                                        <img
                                                            src={person.picture.large}
                                                            alt="Profile"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </button>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Image Cropper Modal */}
            <ImageCropper
                isOpen={showImageCropper}
                onClose={() => setShowImageCropper(false)}
                onImageUploaded={(imageUrl) => {
                    updateConfig({
                        whatsappConfig: {
                            ...widgetConfig.whatsappConfig,
                            profilePicture: imageUrl
                        }
                    });
                }}
                aspectRatio={1}
                title="Upload Profile Picture"
                description="Upload and crop your WhatsApp profile picture. The image will be optimized and stored securely."
            />
        </div>
    );
};

export default WhatsAppWidgetConfig;
